<template>
  <div>
    <!-- Chargement : distinct de l'état vide, et annoncé par du texte, pas par le seul spinner (RGAA) -->
    <div v-if="loading" class="text-center py-10" aria-live="polite" aria-busy="true">
      <v-progress-circular indeterminate aria-label="Chargement des actions"></v-progress-circular>
      <p class="text-body-2 grey--text text--darken-1 mt-3 mb-0">Chargement des actions en cours…</p>
    </div>

    <template v-else>
      <!-- État vide explicite : l'information passe par un texte, l'icône est décorative (RGAA) -->
      <div v-if="isEmpty" class="d-flex flex-column align-center py-10 empty-state" aria-live="polite">
        <v-icon large aria-hidden="true">mdi-clipboard-text-off-outline</v-icon>
        <h2 class="text-h6 font-weight-bold mt-3 mb-1 text-center">{{ emptyTitle }}</h2>
        <p class="text-body-1 grey--text text--darken-1 mb-0 text-center">{{ emptyMessage }}</p>
      </div>

      <DsfrPagination class="mb-6" v-model="page" :length="pageCount" v-if="showPagination" />

      <v-row v-if="!isEmpty || canCreateAction">
        <v-col cols="12" sm="6" md="4" height="100%" v-for="action in actions_list" :key="`action-${action.id}`">
          <ActionCard class="fill-height" :study="action" />
        </v-col>
        <v-col cols="12" sm="6" md="4" height="100%" class="d-flex flex-column" v-if="canCreateAction">
          <v-card
            class="d-flex flex-column align-center justify-center dsfr"
            outlined
            min-height="220"
            height="80%"
            :to="{ name: 'CreateAction' }"
          >
            <v-icon size="100" class="primary--text" aria-hidden="true">mdi-plus</v-icon>
            <v-card-text class="font-weight-bold pt-0 text-center primary--text text-body-1">
              Lancer une nouvelle action
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <DsfrPagination class="my-6" v-model="page" :length="pageCount" v-if="showPagination" />
    </template>
  </div>
</template>

<script>
import ActionCard from "./ActionCard"
import DsfrPagination from "@/components/DsfrPagination"

/**
 * Liste paginée des actions du plan d'actions.
 *
 * Depuis que `/actions` est ouvert au rôle CONSUMER et que l'API filtre la liste sur les
 * rôles effectifs, un convive peut arriver ici avec zéro action. La page doit alors dire
 * explicitement pourquoi elle est vide, et distinguer « chargement » de « aucune action ».
 */
export default {
  name: "ActionPlanPagination",
  components: { ActionCard, DsfrPagination },
  data() {
    return {
      limit: 6,
      page: null,
      actions_list: null,
      actionCount: null,
      canteenName: null,
      searchTerm: null,
      options: {
        sortBy: [],
        sortDesc: [],
        page: 1,
      },
      inProgress: null,
    }
  },
  computed: {
    loading() {
      return this.actions_list === null
    },
    isEmpty() {
      return !this.loading && this.actionCount === 0
    },
    isConsumer() {
      return this.loggedUser.job === "CONSUMER"
    },
    // Mêmes rôles que la garde de route `CreateAction` et que le bouton de `ActionPlan/index.vue` :
    // la carte ne doit pas proposer un écran que la route refuse (sinon clic → redirection
    // dashboard). Le déploiement d'une action est une écriture sur la cantine.
    canCreateAction() {
      return ["MANAGER", "SUPERVISOR", "RESEARCHER"].indexOf(this.loggedUser.job) !== -1
    },
    emptyTitle() {
      return this.isConsumer ? "Aucune action ne vous concerne pour le moment" : "Aucune action pour le moment"
    },
    emptyMessage() {
      if (this.isConsumer) {
        return "Votre cantine ne vous a encore associé à aucun challenge ni à aucune étude. Revenez sur cette page dès qu'une action vous sera proposée."
      }
      if (this.canCreateAction) {
        return "Aucune action n'a encore été créée. Utilisez « Lancer une nouvelle action » pour démarrer la première."
      }
      return "Aucune action n'a encore été créée pour votre établissement."
    },
    pageCount() {
      return Math.ceil(this.actionCount / this.limit) || 0
    },
    showPagination() {
      return this.actionCount > this.limit
    },
    offset() {
      return (this.page - 1) * this.limit
    },
    userPermissions() {
      return this.$store.state.userPermissions
    },
    loggedUser() {
      return this.$store.state.loggedUser
    },
  },
  methods: {
    search() {
      if (this.searchTerm && this.options.page !== 1) {
        this.options.page = 1
      } else {
        this.$router.push({ query: this.getUrlQueryParams() }).catch(() => {})
      }
    },
    getUrlQueryParams() {
      const urlQueryParams = { page: this.options.page }
      if (this.searchTerm) {
        urlQueryParams.recherche = this.searchTerm
      }
      return urlQueryParams
    },
    fetchCurrentPage() {
      const urlQueryParams = { page: this.options.page }
      if (this.searchTerm) {
        urlQueryParams.recherche = this.searchTerm
      }
      this.$store
        .dispatch("fetchAction", { offset: this.offset, search: this.searchTerm })
        .then((response) => {
          this.actions_list = response || []
          this.actionCount = this.actions_list.length
        })
        .catch(() => {
          this.actions_list = []
          this.actionCount = 0
          this.$store.dispatch("notifyServerError")
        })
    },
    clearSearch() {
      this.searchTerm = ""
      this.search()
    },
    updateRoute() {
      const query = { page: this.page }
      if (this.$route.query.page) {
        this.$router.push({ query }).catch(() => {})
      } else {
        this.$router.replace({ query }).catch(() => {})
      }
    },
    populateParameters() {
      this.page = this.$route.query.page ? parseInt(this.$route.query.page, 10) : 1
    },
  },
  watch: {
    page() {
      this.updateRoute()
    },
    $route(newRoute, oldRoute) {
      this.actions_list = null
      if (newRoute.query.etiquette !== oldRoute.query.etiquette) {
        this.actionCount = null
      }
      this.populateParameters()
      this.fetchCurrentPage()
    },
  },
  mounted() {
    // Fetch inconditionnel : sans query d'URL, la liste restait à `null` et l'état de
    // chargement ne se résolvait jamais en état vide.
    this.populateParameters()
    this.fetchCurrentPage()
  },
}
</script>
